import { SnapStyleAPIClient } from "./api-client";
import { SnapStyleConfig } from "./types";

interface TryOnHistoryItem {
    resultUrl: string;
    originalPhotoUrl: string;
    productTitle: string;
    createdAt: string;
}

/**
 * Manages try-on creation, polling with progress simulation, and history.
 */
export class TryOnManager {
    private client: SnapStyleAPIClient;

    constructor(client: SnapStyleAPIClient) {
        this.client = client;
    }

    /**
     * Loads completed try-ons as data (no DOM manipulation).
     */
    async loadHistory(): Promise<TryOnHistoryItem[]> {
        try {
            const data = await this.client.listTryOns(20, 0, "completed");
            return data.tryons
                .filter((t) => t.resultUrl)
                .map((t) => ({
                    resultUrl: t.resultUrl || "",
                    originalPhotoUrl: t.photoUrl || "",
                    productTitle: t.productTitle || "Product",
                    createdAt: t.createdAt || new Date().toISOString(),
                }));
        } catch (error) {
            console.error("Error loading try-on history:", error);
            return [];
        }
    }

    /**
     * Creates a try-on and polls until complete.
     * Calls onProgress with simulated percentage (0-100) tuned for ~30s average.
     * Returns the result URL or throws on failure.
     */
    async processTryOn(
        photoId: string,
        config: SnapStyleConfig,
        onProgress: (percent: number) => void,
    ): Promise<string> {
        // Create the try-on request
        const tryon = await this.client.createTryOn({
            photoId,
            platformProductId: config.productId,
            productTitle: config.productTitle,
            productImageUrl: config.productImageUrl,
            variantId: config.variantId,
        });

        // Start progress simulation (tuned for ~30s average response)
        const progressController = this.startProgressSimulation(onProgress);

        try {
            const completed = await this.client.waitForTryOn(tryon.id);

            // Stop simulation and jump to 100%
            progressController.stop();
            onProgress(100);

            if (completed.status === "completed" && completed.resultUrl) {
                return completed.resultUrl;
            } else {
                throw new Error(completed.errorMessage || "Try-on failed");
            }
        } catch (error) {
            progressController.stop();
            throw error;
        }
    }

    /**
     * Simulates smooth progress for ~30s average API response time.
     * Phase 1: 0 → 20% in ~3s (fast start)
     * Phase 2: 20 → 80% in ~20s (slow steady)
     * Phase 3: 80 → 95% in ~7s (slowing down)
     * Stays at 95% until actual completion.
     */
    private startProgressSimulation(onProgress: (percent: number) => void): { stop: () => void } {
        let currentProgress = 0;
        let stopped = false;

        const phases = [
            { target: 20, duration: 3000, interval: 100 },   // 0→20% in 3s
            { target: 80, duration: 20000, interval: 200 },   // 20→80% in 20s
            { target: 95, duration: 7000, interval: 250 },     // 80→95% in 7s
        ];

        const runPhases = async () => {
            for (const phase of phases) {
                if (stopped) return;

                const startProgress = currentProgress;
                const progressRange = phase.target - startProgress;
                const steps = phase.duration / phase.interval;
                const increment = progressRange / steps;

                for (let i = 0; i < steps; i++) {
                    if (stopped) return;
                    await new Promise((resolve) => setTimeout(resolve, phase.interval));
                    if (stopped) return;

                    currentProgress = Math.min(phase.target, currentProgress + increment);
                    onProgress(Math.round(currentProgress));
                }
            }
        };

        runPhases().catch(() => {
            stopped = true;
        });

        return {
            stop: () => {
                stopped = true;
            },
        };
    }
}
